// Copyright (c) 2020 embyt GmbH. All rights reserved.

import { IOutput, OutputMode } from "parameter";
import { IStateDataOutput } from "states/datastates";
import { isSet, roundToMinDigits } from "utils";

export enum IOState {
  OFF,
  ON,
  UNKNOWN,
  OFFLINE,
}

enum IoStateLogical {
  LINX_REQ_STATE = 1,
  OVERRIDE = 4,
  MANUAL = 16,
}

const DIGITS_TO_SHOW = 3;

class Output {
  readonly parameter: IOutput;
  readonly value: IStateDataOutput | undefined;
  readonly state: IOState;
  readonly isOverride: boolean | undefined;

  constructor(parameter: IOutput, value: IStateDataOutput | undefined) {
    this.parameter = parameter;
    this.value = value;
    this.state = this.deriveState();
    this.isOverride = this.deriveOverrideStatus();
  }

  public getText(): string {
    if (this.state === IOState.OFFLINE || this.value?.logical === undefined) {
      return "offline";
    }
    const value = `${roundToMinDigits(this.value.logical, DIGITS_TO_SHOW)}`;
    return isSet(this.parameter.unit) ? `${value} ${this.parameter.unit}` : value;
  }

  public deriveState() {
    let state = IOState.UNKNOWN;
    // no value at all
    if (this.value?.logical === undefined) {
      state = IOState.OFFLINE;
    }
    // we have at least a logical state available
    else if (this.parameter.mode === OutputMode.ANALOG) {
      // state unknown for analog outputs
      state = IOState.UNKNOWN;
    }
    // digital or PWM outputs
    // check for offline output
    else if (
      this.parameter.mode !== OutputMode.PWM &&
      (this.value.logical & IoStateLogical.OVERRIDE) > 0
    ) {
      state = IOState.OFFLINE;
    }
    // online digital output
    else if (isSet(this.value.physical) && !isNaN(this.value.physical)) {
      // physical value sometimes gets to something like -5E-39...
      // also, physical state may be -1 in case of LIOBs
      const boolValue = Math.abs(this.value.physical) > 1e-10;
      const invert = this.parameter.mode === OutputMode.OPEN;
      state = boolValue !== invert ? IOState.ON : IOState.OFF;
    } else {
      // physical value not available
      state = IOState.OFFLINE;
    }
    return state;
  }

  private deriveOverrideStatus() {
    if (![OutputMode.ANALOG, OutputMode.PWM].includes(this.parameter.mode)) {
      // override flag
      return isSet(this.value?.logical) && (this.value!.logical & 0x4) > 0;
    } else if (this.parameter.mode === OutputMode.PWM) {
      // no override info available for PWM outputs
      return undefined;
    }
    // analog outputs do not have override
    return false; // default
  }
}

export default Output;
